// Master only — voice profile CRUD. Tek modal pattern.

import { type ReactNode, useEffect, useRef, useState } from 'react';
import { supabase, type VoiceProfile } from '../lib/supabase';
import { logAdminAction } from '../lib/audit';
import { AlertTriangle, Plus, Pencil, X, Loader2, Play, Pause } from 'lucide-react';

type Category = 'haber' | 'belgesel' | 'reklam' | 'genel';

const CATEGORY_LABELS: Record<Category, string> = {
  haber: 'Haber',
  belgesel: 'Belgesel',
  reklam: 'Reklam',
  genel: 'Genel',
};

interface FormState {
  slug: string;
  display_name: string;
  elevenlabs_voice_id: string;
  category: Category;
  description: string;
  stability: number;
  similarity_boost: number;
  style: number;
  use_speaker_boost: boolean;
  display_order: number;
  is_active: boolean;
}

const FORM_DEFAULTS: FormState = {
  slug: '',
  display_name: '',
  elevenlabs_voice_id: '',
  category: 'genel',
  description: '',
  stability: 0.5,
  similarity_boost: 0.75,
  style: 0.3,
  use_speaker_boost: true,
  display_order: 10,
  is_active: true,
};

function translateRpcError(msg: string): string {
  if (msg.includes('master_admin_only')) return 'Bu işlem sadece master admin tarafından yapılabilir.';
  if (msg.includes('out_of_range')) return 'Slider değeri 0–1 aralığında olmalı.';
  if (msg.includes('slug_exists')) return 'Bu slug zaten kullanımda.';
  return msg;
}

export default function Voices() {
  const [voices, setVoices] = useState<VoiceProfile[]>([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<VoiceProfile | null>(null);
  const [form, setForm] = useState<FormState>(FORM_DEFAULTS);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [formSuccess, setFormSuccess] = useState<string | null>(null);
  const [playingId, setPlayingId] = useState<string | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  const loadVoices = () => {
    setLoading(true);
    void supabase
      .from('voice_profiles')
      .select('*')
      .order('display_order')
      .then(({ data }) => {
        setVoices((data ?? []) as VoiceProfile[]);
        setLoading(false);
      });
  };

  useEffect(() => { loadVoices(); }, []);

  function openCreate() {
    setEditTarget(null);
    setForm(FORM_DEFAULTS);
    setFormError(null);
    setFormSuccess(null);
    setModalOpen(true);
  }

  function openEdit(v: VoiceProfile) {
    setEditTarget(v);
    setForm({
      slug: v.slug,
      display_name: v.display_name,
      elevenlabs_voice_id: v.elevenlabs_voice_id,
      category: (v.category ?? 'genel') as Category,
      description: v.description ?? '',
      stability: v.stability,
      similarity_boost: v.similarity_boost,
      style: v.style,
      use_speaker_boost: v.use_speaker_boost,
      display_order: v.display_order,
      is_active: v.is_active,
    });
    setFormError(null);
    setFormSuccess(null);
    setModalOpen(true);
  }

  function closeModal() {
    setModalOpen(false);
    setEditTarget(null);
    setFormError(null);
    setFormSuccess(null);
  }

  function setField<K extends keyof FormState>(key: K, value: FormState[K]) {
    setForm((prev) => ({ ...prev, [key]: value }));
  }

  async function handleSave() {
    if (!form.slug.trim() || !form.display_name.trim() || !form.elevenlabs_voice_id.trim()) {
      setFormError('Slug, görünen ad ve ElevenLabs Voice ID zorunlu.');
      return;
    }
    setSaving(true);
    setFormError(null);
    setFormSuccess(null);

    const { error } = await supabase.rpc('upsert_voice_profile', {
      p_slug: form.slug.trim(),
      p_display_name: form.display_name.trim(),
      p_elevenlabs_voice_id: form.elevenlabs_voice_id.trim(),
      p_category: form.category,
      p_description: form.description.trim() || null,
      p_stability: form.stability,
      p_similarity_boost: form.similarity_boost,
      p_style: form.style,
      p_use_speaker_boost: form.use_speaker_boost,
      p_is_active: form.is_active,
      p_display_order: form.display_order,
    });

    if (error) {
      setFormError(translateRpcError(error.message));
      setSaving(false);
      return;
    }

    const isCreate = editTarget === null;
    await logAdminAction({
      action: isCreate ? 'voice.create' : 'voice.update',
      target_type: 'voice_profile',
      target_label: form.display_name.trim(),
      metadata: { slug: form.slug.trim() },
    });

    setFormSuccess(isCreate ? 'Ses profili oluşturuldu.' : 'Ses profili güncellendi.');
    setSaving(false);
    loadVoices();
    setTimeout(() => closeModal(), 900);
  }

  function stopAudio() {
    audioRef.current?.pause();
    audioRef.current = null;
    setPlayingId(null);
  }

  function toggleSample(v: VoiceProfile) {
    if (!v.sample_audio_url) return;
    if (playingId === v.id) {
      stopAudio();
      return;
    }
    stopAudio();
    const a = new window.Audio(v.sample_audio_url);
    a.onended = () => setPlayingId(null);
    audioRef.current = a;
    void a.play();
    setPlayingId(v.id);
  }

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-5xl">
      <div className="flex items-center justify-between gap-3 flex-wrap mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Sesler</h1>
          <p className="text-slate-500 text-sm">Voice profiles ve ElevenLabs ayarları.</p>
        </div>
        <button
          onClick={openCreate}
          className="inline-flex items-center gap-1.5 px-4 py-2 bg-[color:var(--color-primary)] hover:bg-[color:var(--color-primary-soft)] text-white rounded-lg text-sm font-medium shadow-sm"
        >
          <Plus size={14} /> Yeni ses
        </button>
      </div>

      {loading ? (
        <p className="text-slate-400">Yükleniyor…</p>
      ) : voices.length === 0 ? (
        <p className="text-slate-400">Henüz ses profili yok.</p>
      ) : (
        <div className="grid md:grid-cols-2 gap-4">
          {voices.map((v) => (
            <div key={v.id} className="bg-white border border-slate-200 rounded-xl p-5">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <h3 className="font-semibold text-slate-800">{v.display_name}</h3>
                  <p className="text-xs text-slate-500 font-mono">{v.slug} · {v.category ?? '—'}</p>
                </div>
                <div className="flex items-center gap-1.5 shrink-0">
                  {v.elevenlabs_voice_id.startsWith('PLACEHOLDER_') ? (
                    <span className="inline-flex items-center gap-1 text-xs text-amber-700 bg-amber-50 px-2 py-0.5 rounded">
                      <AlertTriangle size={11} /> Kalibre edilmedi
                    </span>
                  ) : v.is_active ? (
                    <span className="text-xs text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded">Aktif</span>
                  ) : (
                    <span className="text-xs text-slate-500 bg-slate-100 px-2 py-0.5 rounded">Pasif</span>
                  )}
                  <button
                    onClick={() => openEdit(v)}
                    className="p-1.5 rounded hover:bg-slate-100 text-slate-500 hover:text-slate-800"
                    title="Düzenle"
                  >
                    <Pencil size={14} />
                  </button>
                </div>
              </div>

              {v.description && <p className="text-sm text-slate-600 mt-2">{v.description}</p>}

              <dl className="mt-3 grid grid-cols-3 gap-2 text-xs">
                <Stat label="stability" value={v.stability.toFixed(2)} />
                <Stat label="similarity" value={v.similarity_boost.toFixed(2)} />
                <Stat label="style" value={v.style.toFixed(2)} />
              </dl>

              <p className="text-xs text-slate-400 font-mono mt-2 truncate">{v.elevenlabs_voice_id}</p>

              {v.sample_audio_url && (
                <button
                  onClick={() => toggleSample(v)}
                  className="mt-3 inline-flex items-center gap-1.5 text-xs text-[color:var(--color-primary)] hover:underline"
                >
                  {playingId === v.id ? <><Pause size={12} /> Durdur</> : <><Play size={12} /> Örnek dinle</>}
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Modal */}
      {modalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between px-6 pt-5 pb-4 border-b border-slate-100">
              <h2 className="font-semibold text-slate-800">
                {editTarget ? 'Ses profilini düzenle' : 'Yeni ses profili'}
              </h2>
              <button onClick={closeModal} className="p-1 rounded hover:bg-slate-100 text-slate-500">
                <X size={18} />
              </button>
            </div>

            <div className="px-6 py-5 space-y-4">
              {/* Slug */}
              <Field label="Slug" required>
                <input
                  type="text"
                  value={form.slug}
                  onChange={(e) => setField('slug', e.target.value.toLowerCase().replace(/\s+/g, '-'))}
                  readOnly={!!editTarget}
                  placeholder="haber"
                  className={`w-full border rounded-lg px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-[color:var(--color-primary)] ${editTarget ? 'bg-slate-50 text-slate-500 cursor-not-allowed' : 'border-slate-300'}`}
                />
                {!!editTarget && <p className="text-xs text-slate-400 mt-1">Slug düzenleme modunda değiştirilemez.</p>}
              </Field>

              {/* Display name */}
              <Field label="Görünen ad" required>
                <input
                  type="text"
                  value={form.display_name}
                  onChange={(e) => setField('display_name', e.target.value)}
                  placeholder="Haber sesi"
                  className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[color:var(--color-primary)]"
                />
              </Field>

              {/* ElevenLabs voice ID */}
              <Field label="ElevenLabs Voice ID" required>
                <input
                  type="text"
                  value={form.elevenlabs_voice_id}
                  onChange={(e) => setField('elevenlabs_voice_id', e.target.value.trim())}
                  placeholder="21m00Tcm4TlvDq8ikWAM"
                  className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-[color:var(--color-primary)]"
                />
                <p className="text-xs text-slate-400 mt-1">ElevenLabs kütüphanesinden kopyala-yapıştır.</p>
              </Field>

              {/* Category */}
              <Field label="Kategori">
                <select
                  value={form.category}
                  onChange={(e) => setField('category', e.target.value as Category)}
                  className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[color:var(--color-primary)]"
                >
                  {(Object.entries(CATEGORY_LABELS) as [Category, string][]).map(([k, lbl]) => (
                    <option key={k} value={k}>{lbl}</option>
                  ))}
                </select>
              </Field>

              {/* Description */}
              <Field label="Açıklama">
                <textarea
                  value={form.description}
                  onChange={(e) => setField('description', e.target.value)}
                  rows={2}
                  placeholder="Net/profesyonel spiker tonu…"
                  className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[color:var(--color-primary)] resize-none"
                />
              </Field>

              {/* Sliders */}
              <SliderField
                label="Stability"
                value={form.stability}
                onChange={(v) => setField('stability', v)}
              />
              <SliderField
                label="Similarity Boost"
                value={form.similarity_boost}
                onChange={(v) => setField('similarity_boost', v)}
              />
              <SliderField
                label="Style"
                value={form.style}
                onChange={(v) => setField('style', v)}
              />

              {/* Speaker boost */}
              <div className="flex items-center justify-between">
                <span className="text-xs font-semibold text-slate-600 uppercase tracking-wider">Speaker Boost</span>
                <ToggleSwitch
                  checked={form.use_speaker_boost}
                  onChange={(v) => setField('use_speaker_boost', v)}
                />
              </div>

              {/* Display order */}
              <Field label="Sıra">
                <input
                  type="number"
                  value={form.display_order}
                  min={1}
                  max={999}
                  onChange={(e) => setField('display_order', Number(e.target.value))}
                  className="w-24 border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[color:var(--color-primary)]"
                />
              </Field>

              {/* is_active — sadece edit modda */}
              {editTarget && (
                <div className="flex items-center justify-between">
                  <span className="text-xs font-semibold text-slate-600 uppercase tracking-wider">Aktif</span>
                  <ToggleSwitch
                    checked={form.is_active}
                    onChange={(v) => setField('is_active', v)}
                  />
                </div>
              )}

              {formError && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700 flex items-start gap-2">
                  <AlertTriangle size={15} className="shrink-0 mt-0.5" />
                  <span>{formError}</span>
                </div>
              )}
              {formSuccess && (
                <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 text-sm text-emerald-700">
                  {formSuccess}
                </div>
              )}
            </div>

            <div className="px-6 pb-5 flex justify-end gap-2">
              <button
                onClick={closeModal}
                disabled={saving}
                className="px-4 py-2 text-sm text-slate-600 hover:text-slate-800 disabled:opacity-40"
              >
                Vazgec
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className="inline-flex items-center gap-2 px-4 py-2 bg-[color:var(--color-primary)] hover:bg-[color:var(--color-primary-soft)] text-white rounded-lg text-sm font-medium disabled:opacity-40"
              >
                {saving && <Loader2 size={14} className="animate-spin" />}
                {saving ? 'Kaydediliyor…' : 'Kaydet'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// -- Alt bileşenler --

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="bg-slate-50 rounded px-2 py-1">
      <dt className="text-slate-500 text-[10px] uppercase">{label}</dt>
      <dd className="font-mono text-slate-700 text-xs">{value}</dd>
    </div>
  );
}

function Field({ label, required, children }: { label: string; required?: boolean; children: ReactNode }) {
  return (
    <div>
      <label className="text-xs font-semibold text-slate-600 uppercase tracking-wider mb-1.5 block">
        {label}{required && <span className="text-red-500 ml-0.5">*</span>}
      </label>
      {children}
    </div>
  );
}

function SliderField({ label, value, onChange }: { label: string; value: number; onChange: (v: number) => void }) {
  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <span className="text-xs font-semibold text-slate-600 uppercase tracking-wider">{label}</span>
        <span className="text-xs font-mono text-slate-700">{value.toFixed(2)}</span>
      </div>
      <input
        type="range"
        min={0}
        max={1}
        step={0.05}
        value={value}
        onChange={(e) => onChange(parseFloat(e.target.value))}
        className="w-full accent-[color:var(--color-primary)]"
      />
    </div>
  );
}

function ToggleSwitch({ checked, onChange }: { checked: boolean; onChange: (v: boolean) => void }) {
  return (
    <button
      type="button"
      onClick={() => onChange(!checked)}
      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${checked ? 'bg-[color:var(--color-primary)]' : 'bg-slate-300'}`}
    >
      <span
        className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${checked ? 'translate-x-6' : 'translate-x-1'}`}
      />
    </button>
  );
}
